import React from 'react';
import { View, Text, TouchableWithoutFeedback, StyleSheet, Linking, Alert } from 'react-native';
import { Svg as SvgElement } from 'react-native-svg';
import i18n from 'i18n-js';

import { machineStatusColorName, machineStatusName, machineStatuses, subStatusName } from './functions';
import { localTime, ncFileStatuses, Popup, isWeb, isPhone } from '@helpers';

import { BottomTriangle, TopTriangle } from '@components/common/popupSvgTriangles/index';
import ProductionInformation from '@components/productionInformation';
import { fullWidth, scale } from '@components/frame';
import { getLinkingModalConfig } from '@helpers';

import Image from '@v2/context/Image/context';
import { NCFileDetailsScreenName } from '@v2/pages/nc-file/details/navigation/config';
import { NCFileDetailsScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { Perspective } from '@v2/pages/common/enums';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import InputLine from '@components/machines/components/detailsComponents/activeMachine/components/InputLine';
import { connect } from 'react-redux';
import ActiveEventTimes from '@v2/pages/batch/details/modules/batch/components/ProductivityStatusInfo/components/ActiveEventTimes';
import { ItemName, LinkText, Period } from '@v2/pages/batch/details/modules/batch/components/BatchDetailsBlock/BatchDetailsBlockContent';
import { BatchDetailsScreenName } from '@v2/pages/batch/details/navigation/config';
import { BatchDetailsScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import { PartDetailsScreenName } from '@v2/pages/part/details/navigation/config';
import { PartDetailsScreen } from '@v2/pages/part/details/common/enums/partDetails.enum';
import { JobDetailsScreenName } from '@v2/pages/job/navigation/config';
import { JobDetailsScreen } from '@v2/pages/job/common/enums/jobDetails.enum';
import LinkIcon from '@v2/context/ProductionBatch/linkIcon';
import NCFileType from '@components/common/NCFileType';
import Loading from '@components/loading';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import { TimeCategory } from '@interfaces/base';
import { BULLET, possibleReasonsOfSubStatusOffline } from '@v2/pages/common/constants/base.constants';
import ReadyPcsQuantity from '@components/common/ReadyPcsV2/components/ReadyPcsQuantity';
import { filterTimeCategoriesData } from '@v2/pages/common/helpers/filterTimeCategoriesData';

export const Wrapper = ({ clearPopupTimeout, popupType, triggerHidePopup, popupConfig, children }) => {
  if (isWeb) {
    return (
      <div
        onMouseOver={() => {
          clearPopupTimeout(popupType);
        }}
        onMouseOut={() => triggerHidePopup(popupType)}
        style={{
          position: 'absolute',
          zIndex: 10000000,
          top: popupConfig?.position?.top - 10,
          left: popupConfig?.position?.left,
        }}
      >
        {children}
      </div>
    );
  }

  return (
    <View
      style={{
        position: 'absolute',
        zIndex: 10000000,
        top: popupConfig?.position?.top + 15,
        left: popupConfig?.position?.left,
      }}
    >
      {children}
    </View>
  );
};

const NCFileDetailsModal = ({
  C,
  popupType,
  popupConfig,
  popupWidth,
  clearPopupTimeout,
  triggerHidePopup,
  isWithERP,
  selectedItem,
  ncFile,
  batch,
  loading,
  batchLoading,
  eventDetails,
  error,
  setSelectedNCFile,
}) => {
  const { isBottom } = popupConfig;

  const currentJob = batch?.links?.[0];
  const ncFileLinked = eventDetails?.ncFileStatusMain === ncFileStatuses.Linked;
  const ncFileChange = eventDetails?.ncFileStatusMain === ncFileStatuses.Change;
  const ncFileUnknown = eventDetails?.ncFileStatusMain === ncFileStatuses.Unknown;
  const ncFileIgnored = eventDetails?.ncFileStatusMain === ncFileStatuses.Ignored;
  const ncFileSetup = eventDetails?.ncFileStatusMain === ncFileStatuses.Setup;
  const batchLinked = batch?.links?.length > 1;

  const jobsExist = batch?.links && batch?.links?.length;
  const noJobs = jobsExist && batch.links.length === 0;
  const singleJobLinked = jobsExist && batch.links.length === 1;
  const multiJobLinked = jobsExist && batch.links.length > 1;

  const ncFileExist = ncFile?.ncFileId != null;

  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

  const isSubStatusOffline = eventDetails?.subStatus === subStatusName.Offline;

  const onBatchNumberClick = () => {
    navigateToMainDetailsScreen({
      mainScreen: BatchDetailsScreenName,
      subScreen: BatchDetailsScreen.BATCH,
      params: {
        perspective: Perspective.BATCH,
        batchNumber: batch?.batchNumber,
        batchId: eventDetails?.productionBatchId,
        ncFileStatus: eventDetails.ncFileStatusMain,
      },
    });
  };

  const onPartNumberClick = () => {
    navigateToMainDetailsScreen({
      mainScreen: PartDetailsScreenName,
      subScreen: PartDetailsScreen.PART,
      params: {
        perspective: Perspective.PART,
        partId: ncFile?.partId,
        partName: ncFile?.partNumber,
      },
    });
  };

  const onJobNumberClick = () => {
    navigateToMainDetailsScreen({
      mainScreen: JobDetailsScreenName,
      subScreen: JobDetailsScreen.JOBS,
      params: {
        perspective: Perspective.JOBS,
        productionOperationId: currentJob?.productionOperationId,
      },
    });
  };

  const onBatchLinkClick = () => {
    Popup({
      Component: (p) => (
        <ProductionInformation
          {...{
            ...p,
            selectedItem,
            productionBatchId: eventDetails?.productionBatchId,
            selectedNCFileId: eventDetails.ncFileIdMain,
          }}
        />
      ),
      ...getLinkingModalConfig(scale, fullWidth),
    });
  };

  const onNCFileUpdateClick = () => {
    Popup({
      Component: (p) => (
        <NCFileType
          {...{
            ...p,
            selectedItem,
            productionBatchId: eventDetails?.productionBatchId,
            ncFileId: eventDetails.ncFileIdMain,
            ncFileName: eventDetails?.ncFileMain,
          }}
        />
      ),
      ...getLinkingModalConfig(scale, fullWidth),
    });
  };

  const LinkedLines = [
    {
      borderBottomRadius: false,
      borderTopRadius: true,
      data: [],
      onlyLabel: false,
      index: 0,
      i: {
        name: 'Clamping Memo',
        isText: true,
        value: ncFile?.memo,
      },
      loading,
    },

    {
      borderBottomRadius: false,
      borderTopRadius: false,
      data: [],
      onlyLabel: false,
      index: 0,
      i: {
        name: 'Batch',
        ValueComponent: <ItemName {...{ C, onPress: onBatchNumberClick, text: batch?.batchNumber }} />,
      },
      loading: loading || batchLoading,
    },

    {
      borderBottomRadius: false,
      borderTopRadius: false,
      data: [],
      onlyLabel: false,
      index: 0,
      i: {
        name: 'Ready',
        ValueComponent: <ReadyPcsQuantity C={C} batch={batch} />,
      },
      loading: loading || batchLoading,
    },

    {
      borderBottomRadius: false,
      borderTopRadius: false,
      data: [],
      onlyLabel: false,
      index: 0,
      i: {
        name: 'Part',
        ValueComponent: <ItemName {...{ C, onPress: onPartNumberClick, text: ncFile?.partNumber }} />,
      },
      loading: loading,
    },

    {
      borderBottomRadius: true,
      borderTopRadius: false,
      data: [{}],
      onlyLabel: false,
      index: 0,
      i: {
        name: 'Drawing',
        isText: true,
        value: ncFile?.partDrawingNumber,
      },
      loading: loading,
    },
  ];

  const TopLeftPart = [
    {
      borderBottomRadius: true,
      borderTopRadius: true,
      data: [{}],
      onlyLabel: false,
      index: 0,
      i: {
        name: 'NC File Type',
        isText: true,
        value: eventDetails?.ncFileStatusMain,
      },
      loading,
    },
  ];

  const LeftBottomPartNCFileLinked = multiJobLinked
    ? [
        {
          borderBottomRadius: true,
          borderTopRadius: true,
          data: [{}],
          onlyLabel: false,
          index: 0,
          i: {
            name: 'Jobs',
            isText: true,
            value: batch?.links?.length,
          },
          loading,
        },
      ]
    : singleJobLinked
    ? [
        {
          borderBottomRadius: false,
          borderTopRadius: true,
          data: [],
          onlyLabel: false,
          index: 0,
          i: {
            name: 'Job',
            ValueComponent: (
              <ItemName
                {...{ C, onPress: onJobNumberClick, text: `${currentJob?.productionOrderNumber}-${currentJob?.productionOperationNumber}` }}
              />
            ),
          },
          loading,
        },
        {
          borderBottomRadius: false,
          borderTopRadius: false,
          data: [],
          onlyLabel: false,
          index: 0,
          i: {
            name: 'Operation',
            isText: true,
            value: currentJob?.shortDescription,
          },
          loading,
        },
        {
          borderBottomRadius: false,
          borderTopRadius: false,
          data: [],
          onlyLabel: false,
          index: 0,
          i: {
            name: 'Scheduled Quantity',
            ValueComponent: <ReadyPcsQuantity C={C} batch={batch} showOnlyTarget />,
          },
          loading: loading || batchLoading,
        },
        {
          borderBottomRadius: false,
          borderTopRadius: false,
          onlyLabel: false,
          data: [],
          index: 0,
          i: {
            name: 'Scheduled Start',
            ValueComponent: <Period startedOn={currentJob?.scheduledStartDate} C={C} />,
          },
          loading,
        },
        {
          borderBottomRadius: true,
          borderTopRadius: false,
          onlyLabel: false,
          data: [{}],
          index: 0,
          i: {
            name: 'Scheduled End',
            ValueComponent: <Period startedOn={currentJob?.scheduledEndDate} C={C} />,
          },
          loading,
        },
      ]
    : noJobs && isWithERP
    ? [
        {
          borderBottomRadius: true,
          borderTopRadius: true,
          data: [{}],
          onlyLabel: false,
          index: 0,
          i: {
            name: 'Batch Link',
            ValueComponent: (
              <View style={[{ flexDirection: 'row-reverse', gap: scale(9), alignItems: 'center', justifyContent: 'flex-end' }]}>
                <LinkIcon
                  isWithERP={isWithERP}
                  linkStatus={batch?.linkStatus}
                  ncFileStatus={eventDetails?.ncFileStatusMain}
                  width={scale(24)}
                  height={scale(24)}
                />
                <LinkText {...{ C, isWithERP, links: batch?.links, linkStatus: batch?.linkStatus, ncFileStatus: batch?.ncFileStatus }} />
              </View>
            ),
            action: onBatchLinkClick,
          },
          C,
          loading: loading || batchLoading,
          bg: 'Background/Light/Field on modal',
        },
      ]
    : [];

  const LeftBottomPartNCFileUnknown = [
    {
      borderBottomRadius: true,
      borderTopRadius: true,
      data: [{}],
      onlyLabel: false,
      index: 0,
      i: {
        name: 'Update NC File Type',
        ValueComponent: (
          <View style={[{ flexDirection: isPhone ? 'row-reverse' : 'row', gap: scale(9), alignItems: 'center', justifyContent: 'flex-end' }]}>
            <Svg icon="unknownLink" width={scale(24)} height={scale(24)} />
            <Text numberOfLines={1} style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>
              {i18n.t('Unknown')}
            </Text>
          </View>
        ),
        action: onNCFileUpdateClick,
      },
      C,
      loading: loading || batchLoading,
      bg: 'Background/Light/Field on modal highlight',
    },
  ];

  const UnlinkedLines = [
    {
      borderBottomRadius: false,
      borderTopRadius: true,
      data: [],
      onlyLabel: false,
      index: 0,
      i: {
        name: 'Batch',
        ValueComponent: <ItemName {...{ C, onPress: onBatchNumberClick, text: batch?.batchNumber }} />,
      },
      loading: loading || batchLoading,
    },

    {
      borderBottomRadius: true,
      borderTopRadius: false,
      data: [{}],
      onlyLabel: false,
      index: 0,
      i: {
        name: 'Batch Quantity',
        ValueComponent: <ReadyPcsQuantity C={C} batch={batch} />,
      },
      loading: loading || batchLoading,
    },
  ];

  const configuration = (() => {
    switch (eventDetails.ncFileStatusMain) {
      case ncFileStatuses.Linked: {
        return {
          showImage: ncFileExist,
          topRightPart: true,
          bottomLeftPart: LeftBottomPartNCFileLinked,
          bottomRightPart: LinkedLines,
        };
      }
      case ncFileStatuses.Change: {
        return {
          showImage: false,
          topLeftPart: TopLeftPart,
          topRightPart: true,
          bottomLeftPart: null,
          bottomRightPart: null,
        };
      }
      case ncFileStatuses.Setup: {
        return {
          showImage: false,
          topLeftPart: TopLeftPart,
          topRightPart: true,
          bottomLeftPart: null,
          bottomRightPart: null,
        };
      }
      case ncFileStatuses.Unknown: {
        return {
          showImage: ncFileExist,
          topRightPart: true,
          bottomLeftPart: ncFileExist ? LeftBottomPartNCFileUnknown : null,
          bottomRightPart: UnlinkedLines,
        };
      }
      case ncFileStatuses.Unlinked: {
        return {
          showImage: ncFileExist,
          topRightPart: true,
          bottomLeftPart: null,
          bottomRightPart: UnlinkedLines,
        };
      }
      case ncFileStatuses.Ignored: {
        return {
          showImage: false,
          topLeftPart: TopLeftPart,
          topRightPart: true,
          bottomLeftPart: null,
          bottomRightPart: null,
        };
      }

      default: {
        return {
          showImage: false,
          topRightPart: true,
          bottomLeftPart: null,
          bottomRightPart: null,
        };
      }
    }
  })();

  const openNCFile = async () => {
    if (isWeb) {
      if (ncFile?.ncFileId) {
        setSelectedNCFile?.({
          machineStatus: {
            ...ncFile,
            ncFile: eventDetails?.ncFileMain,
            status: machineStatuses.NOTPRODUCTIVE,
            isSearchData: true,
            partDetails: {
              drawing: ncFile.partDrawingNumber,
              partId: ncFile.partId,
              partName: ncFile.partNumber,
              position: ncFile.partDrawingPosition,
              description: ncFile.partDescription,
              revision: ncFile.partDrawingRevision,
            },
          },
        });
      }
      navigateToMainDetailsScreen({
        mainScreen: NCFileDetailsScreenName,
        subScreen: NCFileDetailsScreen.NC_FILE,
        params: {
          perspective: Perspective.NC_FILE,
          ncFileName: eventDetails.ncFileMain,
          ncFileId: eventDetails.ncFileIdMain,
          ncFileStatus: eventDetails.ncFileStatusMain,
        },
      });
    } else {
      const url = `smartfactory://nc-file-details?level=ncFile&manufacture=true&noMachine=true&ncFileId=${eventDetails.ncFileIdMain}`;

      const canOpenURL = await Linking.canOpenURL(url);

      if (canOpenURL) {
        await Linking.openURL(url);
      } else {
        Alert.alert(i18n.t('Download Smart Factory App'));
      }
    }
  };

  const HEADER_HEIGHT = scale(50);
  const STATUS_BLOCK_HEIGHT = scale(42);
  const IMAGE_HEIGHT = scale(252);
  const IMAGE_WIDTH = scale(345);
  const LINE_HEIGHT = scale(42);
  const POPUP_PADDING = scale(15);
  const LINE_TITLE_HEIGHT = scale(18);
  const EVENT_DURATION_MARGIN_TOP = scale(19);

  const shouldDisplayOnlyActualTime = [
    subStatusName.Offline,
    subStatusName.NoNcFileSelected,
    subStatusName.ConnectionFailed,
    subStatusName.MachineConnectorOff,
    subStatusName.NoConnector,
  ].includes(popupConfig.item?.subStatus);

  const eventDurationLineCount = filterTimeCategoriesData({
    batch,
    isWithERP,
    data: [
      { name: TimeCategory.ACTUAL },
      { name: TimeCategory.SCHEDULED },
      { name: TimeCategory.IDEAL },
      { name: TimeCategory.PAID },
    ],
    allowedTimeCategories: shouldDisplayOnlyActualTime ? [TimeCategory.ACTUAL] : undefined,
  }).length;

  const eventDurationBlockHeight = EVENT_DURATION_MARGIN_TOP + LINE_TITLE_HEIGHT + eventDurationLineCount * LINE_HEIGHT;

  const popupHeight = (() => {
    if (loading || error) {
      return 150;
    }

    if (isSubStatusOffline) {
      return 391;
    }

    let heightBase = HEADER_HEIGHT + POPUP_PADDING * 2;
    let leftSideHeight = 0;
    let rightSideHeight = 0;

    if (!ncFileExist) {
      return heightBase + STATUS_BLOCK_HEIGHT + POPUP_PADDING * 2 + eventDurationBlockHeight;
    }

    if (configuration.showImage) {
      leftSideHeight += IMAGE_HEIGHT;
    }

    if (configuration.bottomLeftPart?.length) {
      leftSideHeight += LINE_HEIGHT * configuration.bottomLeftPart?.length + POPUP_PADDING;
    }

    if (configuration.topLeftPart) {
      leftSideHeight += POPUP_PADDING + configuration.topLeftPart.length * LINE_HEIGHT;
    }

    if (configuration.topRightPart) {
      rightSideHeight += STATUS_BLOCK_HEIGHT + eventDurationBlockHeight;
    }

    if (configuration.bottomRightPart) {
      rightSideHeight += LINE_HEIGHT * configuration.bottomRightPart.length + POPUP_PADDING;
    }

    let dynamicHeight = Math.max(leftSideHeight, rightSideHeight);

    return heightBase + (dynamicHeight > 0 ? dynamicHeight + POPUP_PADDING * 2 : dynamicHeight);
  })();

  const { imageData } = ncFile;

  const imageConfiguration = {
    image: {
      documentId: imageData?.documentId,
      imageHash: imageData?.contentHash,
      imageData: {
        isCropped: imageData?.isCropped,
        extensionDerivative: imageData?.extensionDerivative,
        pageNumber: imageData?.pageNumber,
        rotationAngle: imageData?.rotationAngle || 0,
        rotationAngleCrop: imageData?.rotationAngle || 0,
      },
      width: IMAGE_WIDTH,
      height: IMAGE_HEIGHT,
      contextId: eventDetails.ncFileIdMain,
      contextCode: 'c',
      borderRadius: 18,
    },
    onPress: () => open(item),
    showImageControl: true,
    noOverlay: true,
    addImageIconColor: 'Button/Light/Secondary Text',
  };

  const EventTimeComponent = (
    <InputLine
      {...{
        borderBottomRadius: true,
        borderTopRadius: true,
        data: [{}],
        onlyLabel: false,
        flexForRightColumn: 1,
        index: 0,
        i: {
          Component: () => (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <Text style={[font.Body, { flexShrink: 0, color: C('Label/Light/Primary') }]}>{i18n.t('Event Time')}</Text>
              <Text
                numberOfLines={1}
                style={[
                  font['Button 2'],
                  {
                    flex: 1,
                    color: C('Label/Light/Tertiary'),
                    fontSize: scale(16),
                    fontWeight: '400',
                    lineHeight: scale(22),
                    textAlign: 'right',
                  },
                ]}
              >
                {`${localTime(eventDetails.startedOn).format('HH:mm:ss')} - ${localTime(eventDetails.endedOn).format('HH:mm:ss')}`}
              </Text>
            </View>
          ),
        },
        C,
        loading: loading || batchLoading,
        bg: 'Background/Light/Field on modal highlight',
        withoutStretchingForContainer: true,
        layoutForPhone: true,
        borderRadiusValue: 18,
      }}
    />
  );

  return (
    <Wrapper {...{ clearPopupTimeout, popupType, triggerHidePopup, popupConfig }}>
      <SvgElement
        height={popupHeight}
        width={popupWidth}
        style={[
          {
            filter: 'drop-shadow(0 0 30px rgba(0, 0, 0, 0.3))',
            transform: 'translateZ(0)', // Don't remove. Without it drop-shadow won't work for Safari and DuckDuckGo browsers
          },
          !isWeb && {
            shadowColor: '#000',
            shadowOffset: {
              width: 0,
              height: scale(7),
            },
            shadowOpacity: 0.43,
            shadowRadius: 9.51,
          },
        ]}
      >
        {isBottom ? (
          <TopTriangle {...{ popupConfig, popupWidth, popupHeight, C, borderRadius: scale(33) }} />
        ) : (
          <BottomTriangle {...{ popupConfig, popupWidth, popupHeight, C, borderRadius: scale(33) }} />
        )}
      </SvgElement>

      <View
        style={{
          position: 'absolute',
          width: popupWidth,
          height: popupHeight,
          top: 0,
          paddingHorizontal: scale(15),
        }}
        onMouseMove={(e) => e.stopPropagation()}
      >
        {loading && (
          <View style={{ position: 'absolute', zIndex: 1000, width: popupWidth, height: popupHeight, top: 0 }}>
            <Loading background={'transparent'} />
          </View>
        )}
        {error && !loading && (
          <View style={{ width: '100%', height: '100%', justifyContent: 'center', alignContent: 'center', alignItems: 'center' }}>
            <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>{i18n.t('No data')}</Text>
          </View>
        )}

        {!loading && !error ? (
          <View style={[isBottom ? { paddingTop: POPUP_PADDING } : { paddingBottom: POPUP_PADDING }, { flex: 1 }]}>
            <View
              style={{
                width: '100%',
                height: HEADER_HEIGHT,
                borderBottomColor: C('Hairline/Light/No trans'),
                borderBottomWidth: StyleSheet.hairlineWidth,
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              {!!eventDetails.ncFileMain && (
                <TouchableWithoutFeedback onPress={openNCFile}>
                  <View style={[{ flexDirection: 'row', alignItems: 'center', cursor: 'pointer' }]}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(5), marginRight: scale(5) }}>
                      <Text style={[font.Body, { color: C('Link/Light/Primary') }]}>{eventDetails.ncFileMain}</Text>
                      <Svg
                        icon="arrowLight"
                        width={scale(24)}
                        height={scale(24)}
                        color={C('Link/Light/Primary')}
                        transform={[{ translateX: scale(4) }]}
                      />
                    </View>
                    <Text
                      style={[font['Subhead 2'], { color: C('Label/Light/Tertiary'), opacity: eventDetails.ncFileVersionSequenceNumberMain ? 1 : 0 }]}
                    >
                      {i18n.t('Version')} {eventDetails.ncFileVersionSequenceNumberMain}
                    </Text>
                  </View>
                </TouchableWithoutFeedback>
              )}

              <View
                style={{
                  marginLeft: 'auto',
                  flexDirection: 'row',
                  alignItems: 'center',
                  flexDirection: 'row',
                  gap: scale(3),
                }}
              >
                {!isSubStatusOffline && (
                  <View style={{ gap: scale(3), alignItems: 'center', flexDirection: 'row' }}>
                    <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{i18n.t(eventDetails.subStatus)}</Text>
                    <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{BULLET}</Text>
                    <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{i18n.t(eventDetails.executionMode)}</Text>
                    <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{BULLET}</Text>
                  </View>
                )}
                <View
                  style={{
                    paddingHorizontal: scale(10),
                    paddingVertical: scale(2),
                    backgroundColor: C(machineStatusColorName[eventDetails.status]),
                    borderRadius: scale(13),
                  }}
                >
                  <Text style={[font.Headline, { color: C('Label/Light/AlwaysLight Primary') }]}>
                    {i18n.t(isSubStatusOffline ? 'No Machine Data' : machineStatusName[eventDetails.status])}
                  </Text>
                </View>
              </View>
            </View>

            {isSubStatusOffline && (
              <View style={{ width: '100%', marginTop: POPUP_PADDING }}>
                {EventTimeComponent}

                <Text style={[font.Body, { color: C('Label/Light/Primary'), marginTop: scale(24), marginBottom: scale(15) }]}>
                  {i18n.t('During this period, no data was received from the machine')}.
                </Text>
                <View style={{ gap: scale(9) }}>
                  <Text style={[font.Body, { color: C('Label/Light/Tertiary') }]}>{i18n.t('Possible reasons')}:</Text>
                  {possibleReasonsOfSubStatusOffline.map((reason) => (
                    <View key={reason} style={{ flexDirection: 'row', alignItems: 'center', gap: scale(7) }}>
                      <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>{BULLET}</Text>
                      <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>{reason}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {!isSubStatusOffline && (
              <View style={{ width: '100%', marginTop: POPUP_PADDING, flexDirection: !ncFileExist ? 'row-reverse' : 'row', gap: scale(15) }}>
                <View style={[{ flex: 1 }]}>
                  {configuration.showImage && (
                    <View style={[{ width: IMAGE_WIDTH, height: IMAGE_HEIGHT }]}>
                      <Image {...imageConfiguration} />
                    </View>
                  )}

                  {configuration.topLeftPart && (
                    <View>
                      {configuration.topLeftPart?.map((i) => (
                        <InputLine
                          withoutStretchingForContainer
                          layoutForPhone
                          borderRadiusValue={18}
                          bg="Background/Light/Field on modal highlight"
                          C={C}
                          flexForRightColumn={1}
                          {...i}
                          key={i.name}
                        />
                      ))}
                    </View>
                  )}

                  {configuration.bottomLeftPart && (
                    <View style={{ marginTop: POPUP_PADDING }}>
                      {configuration.bottomLeftPart.map((i) => (
                        <InputLine
                          withoutStretchingForContainer
                          layoutForPhone
                          borderRadiusValue={18}
                          bg="Background/Light/Field on modal highlight"
                          C={C}
                          flexForRightColumn={1}
                          {...i}
                          key={i.name}
                        />
                      ))}
                    </View>
                  )}
                </View>

                <View style={{ gap: scale(15), flex: 1 }}>
                  {(!ncFileExist || configuration.topRightPart) && (
                    <View style={[configuration.showImage && ncFileLinked && batchLinked && { height: IMAGE_HEIGHT }]}>
                      {EventTimeComponent}

                      <View style={{ marginTop: scale(19) }}>
                        <ActiveEventTimes
                          {...{
                            C,
                            batch,
                            title: 'EVENT DURATION',
                            withoutShadow: true,
                            bg: 'Background/Light/Field on modal highlight',
                            borderRadius: 18,
                            titleStyles: {
                              paddingLeft: 13,
                            },
                            eventDetails,
                            increaseActualTime: false,
                            allowedTimeCategories: shouldDisplayOnlyActualTime ? [TimeCategory.ACTUAL] : undefined,
                          }}
                        />
                      </View>
                    </View>
                  )}

                  {configuration.bottomRightPart && !!ncFileExist && (
                    <View style={{ flex: 1 }}>
                      {configuration.bottomRightPart.map((i) => (
                        <InputLine
                          withoutStretchingForContainer
                          layoutForPhone
                          borderRadiusValue={18}
                          bg="Background/Light/Field on modal highlight"
                          C={C}
                          flexForRightColumn={1}
                          {...i}
                          key={i.name}
                        />
                      ))}
                    </View>
                  )}
                </View>
              </View>
            )}
          </View>
        ) : null}
      </View>
    </Wrapper>
  );
};

export default connect(({ membership: { isWithERP }, machines: { selectedItem } }) => ({ isWithERP, selectedItem }), {})(NCFileDetailsModal);
